import { pipe, ReadonlyArray } from "fp";

import { Company } from "~/Company";
import * as Log from "~/Log";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Time } from "~/Time";

export const use = ({
  driver,
  dateTime,
}: Log.Request.Query.QueryIdParams): Remote<Log.Log> => {
  const { company } = Company.use();
  const startDay = Time.DateTime.subtract({ dateTime })(1, "days");
  const endDay = Time.DateTime.add({ dateTime })(1, "days");

  const currentDayRemote = Remote.Query.use<Log.Log>(
    Log.Request.Query.key({ driver, dateTime }),
    Log.Request.Query.request({ company, driver, dateTime }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );

  const previousDayRemote = Remote.Query.use<Log.Log>(
    Log.Request.Query.key({ driver, ...startDay }),
    Log.Request.Query.request({ company, driver, ...startDay }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );

  const nextDayRemote = Remote.Query.use<Log.Log>(
    Log.Request.Query.key({ driver, ...endDay }),
    Log.Request.Query.request({ company, driver, ...endDay }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );

  // TODO: Consider how to deal with errors, in particular errors for some but not all of the days.
  const currentDayLog = Remote.toUndefined(currentDayRemote);
  const previousDayLog = Remote.toUndefined(previousDayRemote);
  const nextDayLog = Remote.toUndefined(nextDayRemote);

  if (!currentDayLog) {
    return currentDayRemote;
  }

  if (!previousDayLog) {
    return currentDayRemote;
  }

  if (!nextDayLog) {
    return Remote.success(reduceLogs(previousDayLog, [currentDayLog]));
  }

  return Remote.success(
    reduceLogs(previousDayLog, [currentDayLog, nextDayLog])
  );
};

export const useFromRoute = ({
  driver,
  dateTime,
}: Log.Request.Query.QueryIdParams): Remote<Log.Log> => {
  const { company } = Company.use();
  return Remote.Query.use<Log.Log>(
    Log.Request.Query.key({ driver, dateTime }),
    Log.Request.Query.request({ company, driver, dateTime }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );
};

export const useActiveLogWhen = <A>(route: Log.Route, whenArg: When<A>) =>
  when<A>(useFromRoute(route), whenArg);

export const reduceLogs = (
  target: Log.Log,
  others: readonly Log.Log[]
): Log.Log =>
  ReadonlyArray.reduce<Log.Log, Log.Log>(target, (target, other) => {
    return {
      ...target,
      events: [...target.events, ...other.events],
    };
  })(others);

export type When<A> = {
  readonly unavailable: () => A;
  readonly available: (log: Log.Log) => A;
};

export const when = <A>(
  remote: Remote<Log.Log>,
  { unavailable, available }: When<A>
): A =>
  Remote.when(remote, {
    idle: () => unavailable(),
    error: () => unavailable(),
    loading: () => unavailable(),
    success: (log) => available(log),
  });

export const useWhen = <A>(route: Log.Route, whenArg: When<A>) =>
  when<A>(use(route), whenArg);

export const useWhenAvailable = (route: Log.Route): Log.Log | undefined =>
  pipe(use(route), Remote.toUndefined);

const filterWithinInterval = (events: readonly Log.Event.UniqueEvent[]) => ({
  interval,
}: Time.Interval.Stopped) =>
  pipe(
    events,
    ReadonlyArray.filter<Log.Event.UniqueEvent>((statusEvent) =>
      Time.Interval.Stopped.collides({ interval })(statusEvent)
    )
  );

export const useAllEventsSorted = (
  route: Log.Route
): readonly Log.Event.UniqueEvent[] =>
  Log.Data.useWhen<readonly Log.Event.UniqueEvent[]>(route, {
    unavailable: () => [],
    available: ({ events }) =>
      pipe(
        events,
        ReadonlyArray.map<Log.Event.Event, Log.Event.UniqueEvent>((event) => ({
          id: Log.Event.id(event),
          ...event,
        })),
        ReadonlyArray.sort<Log.Event.UniqueEvent>(
          Order.fromCompare(Time.Interval.compare)
        )
      ),
  });

export const mergeAdjacentLikeEventsSpanningMidnight = (
  events: readonly Log.Event.UniqueEvent[],
  driverTimezone: Time.Zone
) => {
  const eventEndIndex = events.length - 1;
  return pipe(
    events,
    ReadonlyArray.reduceWithIndex<
      Log.Event.UniqueEvent,
      readonly Log.Event.UniqueEvent[]
    >([], (index, reducedEvents, event) => {
      if (
        index < eventEndIndex &&
        Time.DateTime.isEndOfDayForZone(event.interval.stop, driverTimezone)
      ) {
        if (events[index + 1].status == event.status) {
          return [
            ...reducedEvents,
            {
              ...event,
              ...Time.Interval.Stopped.fromDateTimes(
                event.interval.start,
                events[index + 1].interval.stop
              ),
            },
          ];
        }
      } else if (
        index > 1 &&
        Time.DateTime.isStartOfDayForZone(
          event.interval.start,
          driverTimezone
        ) &&
        events[index - 1].status == event.status
      ) {
        // Skip over this event since it was incorporated into the previous event.
        return reducedEvents;
      }
      return [...reducedEvents, event];
    })
  );
};

export const constrainEventsToWindow = (route: Log.Route) => (
  events: readonly Log.Event.UniqueEvent[]
): readonly Log.Event.UniqueEvent[] =>
  pipe(toDataWindow(route), filterWithinInterval(events));

export const toDataWindow = (route: Log.Route) =>
  pipe(route, ({ dateTime }) =>
    Time.Interval.Stopped.fromDateTimes(
      Time.DateTime.subtract({ dateTime })(12, "hours"),
      Time.DateTime.add({ dateTime })(36, "hours")
    )
  );
